import React, { useCallback, useEffect } from 'react';
import { Grid, Autocomplete, TextField, Checkbox } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { FormProps, getRequiredValidationRule, getValidationProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { ApplicationQuestionOption } from 'store/types/ApplicationQuestion';
import { SelectQuestionOption } from 'store/types/SelectOption';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import styles from './MultiSelectQuestionFormItem.module.scss';

interface MultiSelectQuestionFormItemProps extends FormProps {
  fieldName: string;
  options?: ApplicationQuestionOption[];
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const MultiSelectQuestionFormItem: React.FunctionComponent<MultiSelectQuestionFormItemProps> = ({
  fieldName,
  options = [],
  required,
  disabled,
}) => {
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext<{ [fieldName: string]: SelectQuestionOption[] }>();
  const { update } = useFieldArray({ control, name: fieldName });
  const [value, setValue] = React.useState<ApplicationQuestionOption[]>([]);
  const [inputValue, setInputValue] = React.useState('');

  const handleChange = useCallback(
    (data: ApplicationQuestionOption[]) => {
      setValue(data);

      const fieldValues = getValues()[fieldName];
      fieldValues.map((option, index) => {
        const isSelected = data.some((v) => v.name === option.name);
        update(index, { ...option, value: isSelected });
      });
    },
    [fieldName, getValues, update]
  );

  useEffect(() => {
    const fieldValues = getValues()[fieldName];
    setValue(options.filter((option) => fieldValues.some((v) => v.name === option.name && v.value)));
  }, [getValues, fieldName, options]);

  return (
    <Grid {...defaultGridContainerProps}>
      <Grid {...defaultGridItemProps}>
        <Controller
          render={({ field }) => (
            <Autocomplete
              value={value}
              inputValue={inputValue}
              multiple
              disableCloseOnSelect
              options={options}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.name === value.name}
              disabled={disabled}
              classes={{ tag: styles.customTag }}
              sx={{
                '.MuiChip-deleteIcon': {
                  color: 'white !important',
                },
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...getValidationProps(fieldName, errors)}
                  inputRef={field.ref}
                  required={required}
                />
              )}
              onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              onChange={(_event, data) => {
                handleChange(data);
              }}
            />
          )}
          name={fieldName}
          control={control}
          rules={{
            required: getRequiredValidationRule('your answer', true, required),
          }}
        />
      </Grid>
    </Grid>
  );
};
export default MultiSelectQuestionFormItem;
